import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useSeo } from "../../hooks/useSeo";
import { ARTICLES, COLUMNS, type Article } from "../../mocks/mockContent";
import { formatCount } from "../../utils/format";
import Reveal from "../../components/feature/Reveal";

const PAGE_SIZE = 6;

function ArticleRow({ a }: { a: Article }) {
  return (
    <Reveal>
      <article className="group flex gap-5 border-b hairline py-6">
        <img
          src={a.cover}
          alt={a.title}
          className="hidden h-40 w-60 shrink-0 rounded-xl object-cover object-top sm:block"
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-xs">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-2.5 py-1 font-semibold text-accent-900">
              {a.category}
            </span>
            <span className="text-foreground-500">{a.date}</span>
          </div>
          <h3 className="mt-3 font-display text-xl font-semibold leading-snug text-foreground-950 group-hover:text-primary-700">
            <Link to={`/read/${a.id}`}>{a.title}</Link>
          </h3>
          <p className="mt-2 line-clamp-2 text-sm leading-6 text-foreground-600">{a.summary}</p>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-500">
            <span>{a.author}</span>
            <span className="inline-flex items-center gap-1"><i className="ri-eye-line" aria-hidden="true"></i>{formatCount(a.read)}</span>
            <span className="inline-flex items-center gap-1"><i className="ri-heart-line" aria-hidden="true"></i>{formatCount(a.likes)}</span>
          </div>
        </div>
      </article>
    </Reveal>
  );
}

export default function RexunPage() {
  const [params, setParams] = useSearchParams();
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [kw, setKw] = useState("");

  const cat = params.get("cat") || "all";
  const q = params.get("q") || "";

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [cat, q]);

  const list = useMemo(() => {
    let arr = ARTICLES;
    if (q) {
      const query = q.trim().toLowerCase();
      arr = arr.filter((a) => (a.title + a.summary + a.category).toLowerCase().includes(query));
    } else if (cat !== "all") {
      arr = arr.filter((a) => a.category === cat);
    }
    return arr;
  }, [cat, q]);

  const shown = list.slice(0, visible);
  const catName = q ? `“${q}”` : cat === "all" ? "全部瓜田" : cat;

  const pick = (key: string) => {
    if (key === "all") {
      setParams({}, { replace: true });
    } else {
      setParams({ cat: key }, { replace: true });
    }
  };

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const val = kw.trim();
    if (!val) return;
    setParams({ q: val }, { replace: true });
  };

  const bcLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://uyizhan.cn/" },
      { "@type": "ListItem", position: 2, name: catName, item: "https://uyizhan.cn/rexun" },
    ],
  };

  useSeo({
    title: `${catName} - 吃瓜热闻与栏目资讯聚合 | 瓜田日报`,
    description: `浏览「${catName}」下的最新吃瓜热闻：${shown
      .slice(0, 3)
      .map((a) => a.title)
      .join("、")}。每日更新的明星动态、影视综艺、网络热梗与热搜解读，尽在瓜田日报。`,
    path: `/rexun?cat=${encodeURIComponent(catName)}`,
    type: "collection",
    ld: bcLd,
  });

  return (
    <>
      {/* page intro */}
      <section className="border-b hairline bg-background-100/60">
        <div className="mx-auto max-w-6xl px-4 py-10 md:px-6 md:py-12">
          <nav aria-label="面包屑" className="text-sm text-foreground-500">
            <ol className="flex items-center gap-2">
              <li><Link to="/" className="transition-colors hover:text-primary-700">首页</Link></li>
              <li aria-hidden="true"><i className="ri-arrow-right-s-line"></i></li>
              <li className="text-foreground-800">{q ? "搜索结果" : "吃瓜热闻"}</li>
              {!q && (
                <>
                  <li aria-hidden="true"><i className="ri-arrow-right-s-line"></i></li>
                  <li aria-current="page" className="font-medium text-primary-700">{catName}</li>
                </>
              )}
            </ol>
          </nav>

          <div className="mt-6 flex flex-col justify-between gap-5 lg:flex-row lg:items-end">
            <div>
              <h1 className="font-display text-3xl font-bold text-foreground-950">
                {q ? `“${q}” 的搜索结果` : cat === "all" ? "吃瓜热闻 · 全部瓜田" : `${cat} · 栏目资讯`}
              </h1>
              <p className="mt-3 max-w-2xl text-[15px] leading-7 text-foreground-600">
                {q
                  ? `共为你找到 ${list.length} 条相关资讯，以下内容均已通过编辑部核实与筛选，供你理性阅读。`
                  : "聚合各栏目最新吃瓜内容，支持按栏目筛选。坚持正向、客观、有据可查的报道风格，帮你高效围观公共话题。"}
              </p>
            </div>
            <form onSubmit={submitSearch} role="search" className="w-full max-w-xs">
              <div className="flex items-center rounded-full border border-background-300 bg-background-50 px-3 py-2">
                <i className="ri-search-line text-foreground-500" aria-hidden="true"></i>
                <input
                  value={kw}
                  onChange={(e) => setKw(e.target.value)}
                  type="search"
                  placeholder="站内搜瓜"
                  aria-label="站内搜索"
                  className="w-full bg-transparent pl-2 text-sm outline-none placeholder:text-foreground-400"
                />
              </div>
            </form>
          </div>

          <div className="mt-6 inline-flex flex-wrap gap-1 rounded-full bg-background-200/70 p-1" role="tablist" aria-label="栏目筛选">
            {COLUMNS.map((c) => {
              const isOn = q ? false : cat === c.key;
              return (
                <button
                  key={c.key}
                  onClick={() => pick(c.key)}
                  className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors ${
                    isOn ? "bg-background-50 font-semibold text-primary-700 shadow-soft" : "text-foreground-600 hover:text-primary-700"
                  }`}
                >
                  {c.name}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* results */}
      <section className="bg-background-50">
        <div className="mx-auto max-w-6xl px-4 py-10 md:px-6 md:py-12">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-sm text-foreground-500">
              共 <span className="font-semibold text-primary-700">{list.length}</span> 条 · 已显示 {shown.length} 条
            </p>
          </div>

          {shown.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-2xl border hairline bg-background-100/40 py-20 text-center">
              <i className="ri-inbox-2-line text-5xl text-background-300" aria-hidden="true"></i>
              <p className="mt-4 font-display text-lg font-semibold text-foreground-800">暂未找到相关内容</p>
              <p className="mt-2 text-sm text-foreground-600">换个关键词，或切换到其他栏目再看看</p>
              <button onClick={() => { setParams({}, { replace: true }); setKw(""); }} className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600">
                清空筛选条件
              </button>
            </div>
          ) : (
            <div className="divide-y-0">
              {shown.map((a) => (
                <ArticleRow key={a.id} a={a} />
              ))}
            </div>
          )}

          {visible < list.length && (
            <div className="mt-10 text-center">
              <button
                onClick={() => setVisible((v) => v + PAGE_SIZE)}
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-background-300 bg-background-100 px-6 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-700"
              >
                加载更多
                <i className="ri-arrow-down-line" aria-hidden="true"></i>
              </button>
            </div>
          )}
        </div>
      </section>
    </>
  );
}